import { apiFetch, ApiError } from "./client";

export type Task = {
  id: string;
  /** NULL = общая задача аккаунта, видна при любом выбранном магазине. */
  store_id: string | null;
  /** NULL = общая задача (не привязана к товару). */
  offer_id: string | null;
  listing_key: string | null;
  /** Имя товара на момент создания (денормализация для orphaned tasks). Пусто для общих. */
  product_name: string;
  text: string;
  status: "open" | "done";
  column_id: string | null;
  position: number;
  /** ISO date YYYY-MM-DD когда задача должна быть сделана. NULL = без даты. */
  due_date: string | null;
  created_at: string;
  updated_at: string;
};

/** Конфликт версий задачи — кто-то изменил параллельно с нами. */
export class TaskConflictError extends Error {
  constructor() {
    super("Task was modified elsewhere");
    this.name = "TaskConflictError";
  }
}

export async function fetchTasks(scopeQuery = ""): Promise<Task[]> {
  const data = await apiFetch<{ tasks: Task[] }>(`/api/tasks${scopeQuery}`);
  return data.tasks;
}

export async function addTask(
  offer_id: string | null,
  text: string,
  product_name?: string,
  due_date?: string | null,
  column_id?: string | null,
  listing_key?: string | null,
  /** Магазин страницы важнее активного магазина сессии. */
  store_id?: string | null,
): Promise<Task> {
  return apiFetch<Task>("/api/tasks", {
    method: "POST",
    body: JSON.stringify({
      offer_id,
      ...(listing_key ? { listing_key } : {}),
      ...(store_id !== undefined ? { store_id } : {}),
      text,
      ...(product_name ? { product_name } : {}),
      ...(due_date !== undefined ? { due_date } : {}),
      ...(column_id ? { column_id } : {}),
    }),
  });
}

export async function updateTask(
  id: string,
  patch: { text?: string; status?: "open" | "done"; column_id?: string | null; position?: number; due_date?: string | null },
  expectedUpdatedAt?: string,
  scopeQuery = "",
): Promise<Task> {
  try {
    return await apiFetch<Task>(`/api/tasks${scopeQuery}`, {
      method: "PATCH",
      body: JSON.stringify({ id, ...patch, ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}) }),
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 409 && err.body && typeof err.body === "object" && "code" in err.body && (err.body as { code: string }).code === "TASK_CONFLICT") {
      throw new TaskConflictError();
    }
    throw err;
  }
}

export type TaskColumn = { id: string; name: string; color: string; position: number };

export async function fetchColumns(): Promise<TaskColumn[]> {
  const data = await apiFetch<{ columns: TaskColumn[] }>("/api/task-columns");
  return data.columns;
}

export async function createColumn(name: string, color: string): Promise<TaskColumn> {
  return apiFetch<TaskColumn>("/api/task-columns", { method: "POST", body: JSON.stringify({ name, color }) });
}

export async function updateColumn(id: string, patch: { name?: string; color?: string }): Promise<TaskColumn> {
  return apiFetch<TaskColumn>("/api/task-columns", { method: "PATCH", body: JSON.stringify({ id, ...patch }) });
}

export async function reorderColumns(items: { id: string; position: number }[]): Promise<void> {
  await apiFetch("/api/task-columns", { method: "PATCH", body: JSON.stringify(items) });
}

export async function deleteColumn(id: string): Promise<void> {
  await apiFetch(`/api/task-columns?id=${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function deleteTask(id: string): Promise<void> {
  await apiFetch(`/api/tasks?id=${encodeURIComponent(id)}`, { method: "DELETE" });
}
